import React, { useEffect, useRef, useState } from "react";
import Card from "@mui/material/Card";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { CardContent, Divider, Link } from "@mui/material";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import { compareAsc, differenceInMinutes, format } from "date-fns";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { Content } from "@iventis/translations/content/typed-content";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Body2 } from "@iventis/styles/src/components/texts";
import { POPOVER_VIEWPORT_MARGIN, media, styled } from "@iventis/styles";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { addTimeSpanToDate, timeSpanToTimeDifference, formatDateInTimezone } from "@iventis/utilities";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { CardHeader } from "./card-header";
import { getDisciplineValue, SportsScheduleEntityType } from "./sport-event-helpers";
import { SubEventBlockCardContent } from "./sub-event-card-content";
import { POPOVER_MAX_HEIGHT_LARGE_SCREEN } from "./popover-by-cords";
import { getEventsAtVenue } from "./schedule-helper-functions";
import { getDataFieldDisplayableValue, getSystemDataFieldFromEventBlock, isSystemDataField } from "./data-field-helpers";
import { findEventBlockHierarchyEntitiesByCalendarLevel } from "./hierarchy.helpers";

export const CARD_HEADER_HEIGHT = "72px";

interface VenueWithEvents {
    location: VenueLocation;
    eventUnits: EventBlock[];
}

export const SportSessionCard: React.FC<{
    eventBlockHierarchy: Hierarchy<EventBlock>; //  The event block to show.
    dayNumber?: number; // The Day number of the string. Optional - Will not show if not provided.
    venueLocations: VenueLocation[]; // The relevant venue locations to show for the event block.
    scheduleVersions: ScheduleVersion[]; // The parent schedule versions to the event block.
    onEditSession: boolean;
    onClose?: () => void;
    onEdit?: () => void;
    onEditEventBlockChild?: (id: string) => void;
    onCreateEventBlockChild?: () => void;
    timezone: string;
}> = ({ eventBlockHierarchy, onClose, onEditEventBlockChild, venueLocations, scheduleVersions, onEditSession, onEdit, dayNumber, onCreateEventBlockChild, timezone }) => {
    const translate = useIventisTranslate();
    const createDayValue = () => {
        const stringDate = format(new Date(eventBlockHierarchy.item.startAt), "EEE, dd MMM yyyy");
        return dayNumber != null ? `${translate(Content.schedule.day)} ${dayNumber} (${stringDate})` : stringDate;
    };

    // Set description box height and decided whether to use 'show more' button
    const [showFullDescription, setShowFullDescription] = useState(false);
    // Whether to show the "Show More" button
    const [showDescriptionShowMoreButton, setShowDescriptionShowMoreButton] = useState<boolean>(undefined);

    const { description } = eventBlockHierarchy.item;
    // Lines are 19px high
    const maxLinesHeightPx = 19 * 3;
    const exceedsMaxHeight = (el: HTMLElement) => el.scrollHeight > maxLinesHeightPx;
    const descriptionRef = useRef();

    useEffect(() => {
        // Null state tells the below useEffect that we are ready to calculate the description's height
        setShowDescriptionShowMoreButton(null);
    }, [description]);

    useEffect(() => {
        // If we're ready to calculate the description's height
        if (showDescriptionShowMoreButton === null) {
            // Show the full description
            setShowFullDescription(true);
            const descriptionOverflows = exceedsMaxHeight(descriptionRef.current);
            // If it overflows, hide it
            setShowFullDescription(!descriptionOverflows);
            // Save whether to show the 'show more' button if it does overflow
            setShowDescriptionShowMoreButton(descriptionOverflows);
        }
    }, [descriptionRef?.current, showDescriptionShowMoreButton]);

    const dateTimeInfo: { itemName: string; itemValue: string }[] = [
        { itemName: translate(Content.schedule.session_card.day), itemValue: createDayValue() },
        { itemName: translate(Content.schedule.session_card.start_time), itemValue: format(formatDateInTimezone(eventBlockHierarchy.item.startAt, timezone), "HH:mm:ss") },
        { itemName: translate(Content.schedule.session_card.end_time), itemValue: format(formatDateInTimezone(eventBlockHierarchy.item.endAt, timezone), "HH:mm:ss") },
        {
            itemName: translate(Content.schedule.session_card.duration),
            itemValue: differenceInMinutes(new Date(eventBlockHierarchy.item.endAt), new Date(eventBlockHierarchy.item.startAt)).toString(),
        },
        {
            itemName: translate(Content.schedule.session_card.zero_time),
            itemValue:
                eventBlockHierarchy.item.zeroAt == null
                    ? translate(Content.schedule.create_session_form.inputLabels.zeroCheckBoxLabel)
                    : `${format(
                          formatDateInTimezone(addTimeSpanToDate(eventBlockHierarchy.item.zeroAt, eventBlockHierarchy.item.startAt), timezone),
                          "HH:mm:ss"
                      )} (${timeSpanToTimeDifference(eventBlockHierarchy.item.zeroAt)})`,
        },
    ];

    const venueData: { itemName: string; itemValue: string }[] = venueLocations.map((venue: VenueLocation) => ({
        itemName: translate(venue?.type?.name),
        itemValue: venue?.name,
    }));

    // Get all of the data fields names and values
    const dataFields: { itemName: string; itemValue: string | number }[] = eventBlockHierarchy.item.dataFieldValues
        .filter((dataFieldValue) => !isSystemDataField(dataFieldValue.dataField.id, eventBlockHierarchy.item.type.dataFields))
        .map((dataFieldValue) => {
            const value = getDataFieldDisplayableValue(dataFieldValue);
            return { itemName: dataFieldValue.dataField.name, itemValue: value };
        });

    // get child summary dataFields
    const { childSummaryDataFieldIds } = eventBlockHierarchy.item.type;

    const childDataFieldsToShow = childSummaryDataFieldIds.map((childDataFieldId) => {
        // For each summary datafield id create an array of unique values
        const dataFields: DataFieldValue[] = [];
        // For each child of the event block get the value of the summary datafield
        eventBlockHierarchy.children.forEach((eventBlockChild) => {
            const dataFieldValue = eventBlockChild.item.dataFieldValues.find((dataFieldValue) => dataFieldValue.dataField.id === childDataFieldId);
            if (dataFieldValue != null) {
                const value = getDataFieldDisplayableValue(dataFieldValue);
                // See if we already have this value, if not add it to the array
                if (!dataFields.some((df) => df.value === value)) {
                    dataFields.push({ dataField: dataFieldValue.dataField, value });
                }
            }
        });
        return dataFields;
    });

    // change this just get all the schedule items and map there datafield for events
    const childEventBlocks: EventBlock[] = findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy.children, 2).map(({ item }) => item);
    // Gets every unique venue
    const childEventBlockVenues: VenueLocation[] = childEventBlocks.map((childEventBlock) => childEventBlock.venueLocation);
    const uniqueChildEventBlockVenueIds: string[] = [
        ...new Set(childEventBlockVenues.map((childEventBlockVenue) => (childEventBlockVenue != null ? childEventBlockVenue.id : null))),
    ];
    // Construct array of venues with their corresponding event units
    const eventsAtVenues: VenueWithEvents[] = uniqueChildEventBlockVenueIds.map((venueLocationId) => {
        const venueLocation = childEventBlockVenues.find((childEventBlockVenue) => (childEventBlockVenue != null ? childEventBlockVenue.id === venueLocationId : null));
        return {
            location: venueLocation,
            eventUnits: getEventsAtVenue(childEventBlocks, venueLocation),
        };
    });

    const disciplineSchedule = scheduleVersions.find((sv) => sv.type.name === SportsScheduleEntityType.disciplineSchedule);
    const schedule = scheduleVersions.find((sv) => sv.type.name === SportsScheduleEntityType.schedule);
    // Combination of discipline and session code.
    const codeDataFieldValue = getSystemDataFieldFromEventBlock(EventBlockSystemDataFieldType.Code, eventBlockHierarchy.item)?.value;
    const cardTitle =
        disciplineSchedule != null ? (
            <>
                {getDisciplineValue(disciplineSchedule)}
                <span style={{ fontWeight: "lighter" }}>
                    <span> </span>({codeDataFieldValue ? <span>{codeDataFieldValue}</span> : <span>No code available</span>})
                </span>
            </>
        ) : (
            "No Title"
        );
    const breadcrumb = disciplineSchedule != null && schedule != null ? `${schedule.name} / ${disciplineSchedule.name}` : "";
    return (
        <StyledCard>
            <CardHeader title={cardTitle} subTitle={breadcrumb} onClose={onClose} onEdit={onEdit} canEditSession={onEditSession} />
            <StyledCardContent>
                {dateTimeInfo?.length > 0 && (
                    <>
                        {dateTimeInfo.map((t) => (
                            <StyledRow key={`time-${t.itemName}-${t.itemValue}`}>
                                <ItemName>{t.itemName}</ItemName>
                                <ItemValue>{t.itemValue}</ItemValue>
                            </StyledRow>
                        ))}
                        <StyledDivider />
                    </>
                )}
                {venueData?.length > 0 && (
                    <>
                        {venueData.map((t) => (
                            <StyledRow key={`venue-${t.itemName}-${t.itemValue}`}>
                                <ItemName>{t.itemName}</ItemName>
                                <ItemValue>{t.itemValue}</ItemValue>
                            </StyledRow>
                        ))}
                        <StyledDivider />
                    </>
                )}
                {dataFields?.length > 0 && (
                    <>
                        {dataFields.map((t) => (
                            <StyledRow key={`df-${t.itemName}-${t.itemValue}`}>
                                <ItemName>{t.itemName}</ItemName>
                                <ItemValue>{t.itemValue}</ItemValue>
                            </StyledRow>
                        ))}
                        <StyledDivider />
                    </>
                )}
                <ItemName>Description</ItemName>
                <StyledDescription ref={descriptionRef} style={{ maxHeight: showFullDescription ? "none" : `${maxLinesHeightPx}px` }}>
                    {description}
                </StyledDescription>
                {showDescriptionShowMoreButton && (
                    <DescButton underline="hover" color="inherit" onClick={() => setShowFullDescription(!showFullDescription)}>
                        {showFullDescription ? (
                            <span>{translate(Content.schedule.session_card.show_less)}</span>
                        ) : (
                            <span>{translate(Content.schedule.session_card.show_more)}</span>
                        )}
                        <DescIcon size="sm" style={{ marginLeft: "5px" }} icon={["fas", showFullDescription ? "chevron-up" : "chevron-down"]} />
                    </DescButton>
                )}
                <StyledDivider />
                {childDataFieldsToShow?.length > 0 &&
                    childDataFieldsToShow.map((dataField) =>
                        dataField.map((dataFieldValue, index) => (
                            <>
                                <div key={`dataField-values-${dataFieldValue.value}`}>
                                    {index === 0 && <ItemName>{dataFieldValue.dataField.name}</ItemName>}
                                    <p>{dataFieldValue.value}</p>
                                </div>
                            </>
                        ))
                    )}
                {childDataFieldsToShow[0]?.length > 0 && <StyledDivider />}
                <ItemName>{translate(Content.schedule.session_card.scheduled_items_in_session)}</ItemName>
                {eventsAtVenues.map((venueWithEventUnits: VenueWithEvents) => {
                    const { location, eventUnits } = venueWithEventUnits;
                    const locationHeader = location ? (
                        <VenueHeader key={location.id}>{`${Content.schedule.session_card.in} ${location.name}`}</VenueHeader>
                    ) : (
                        <VenueHeader key="no-space">{translate(Content.schedule.session_card.no_space)}</VenueHeader>
                    );
                    const eventUnitElements = [locationHeader];
                    return eventUnitElements.concat(
                        eventUnits
                            .sort((a, b) => compareAsc(a.startAt, b.startAt)) // Within each venue group, sort by start time
                            .map((eventUnit) => (
                                <SubEventBlockCardContent
                                    key={eventUnit.id}
                                    onEdit={onEditEventBlockChild}
                                    eventBlock={eventUnit}
                                    eventBlockHierarchy={eventBlockHierarchy}
                                    canEditEvent={onEditSession}
                                    timezone={timezone}
                                />
                            ))
                    );
                })}
                {onEditSession && (
                    <InteractiveElement className="add-event-unit" onClick={onCreateEventBlockChild}>
                        <Body2>{translate(Content.schedule.session_card.add_scheduled_item)}</Body2>
                    </InteractiveElement>
                )}
            </StyledCardContent>
        </StyledCard>
    );
};

const VenueHeader = styled.p`
    margin-block: 6px;
`;

const StyledRow = styled.div`
    width: auto;
    margin-block: 6px;
`;

const StyledDivider = styled(Divider)`
    margin-block: 10px;
`;

const ItemName = styled.p`
    display: inline-block;
    font-weight: bold;
    width: 50%;
`;

const StyledDescription = styled.p`
    margin-top: 6px;
    text-overflow: ellipsis;
    width: 100%;
    text-align: justify;
    overflow: hidden;
    white-space: pre-line;
`;

const ItemValue = styled.p`
    display: inline-block;
    width: 50%;
`;

const DescButton = styled(Link)`
    font-weight: bold;
    font-size: ${fontSizes.medium};
    display: inline-block;
    width: 100%;
    text-align: right;
    cursor: pointer;
`;

const DescIcon = styled(FontAwesomeIcon)`
    margin-right: inlineTextIconMargin;
`;

const StyledCard = styled(Card)`
    display: grid;
    grid-template-rows: ${CARD_HEADER_HEIGHT}px auto;
    width: 100%;
`;

const StyledCardContent = styled(CardContent)`
    overflow-y: auto;
    width: auto;
    box-sizing: border-box;
    max-height: calc(100vh - ${POPOVER_VIEWPORT_MARGIN} - ${CARD_HEADER_HEIGHT});
    max-height: calc(100dvh - ${POPOVER_VIEWPORT_MARGIN} - ${CARD_HEADER_HEIGHT});
    ${media.large} {
        max-height: calc(${POPOVER_MAX_HEIGHT_LARGE_SCREEN} - ${CARD_HEADER_HEIGHT});
    }
    .add-event-unit {
        text-align: left;
        text-decoration: underline;
        margin: 10px 0;
        padding: 5px 0;
    }
`;
